/**
 * @Description 营业额预估结论值
 *
 * 25年8月版
 * https://confluence.lanhanba.com/pages/viewpage.action?pageId=149533541
 */

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Form /*, Col*/ } from 'antd';
import { floorKeep, isNotEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { calculateHandle, ControlType, estimateSalesReasonableSelectYHT, getCalculateComs, setComValue } from '../../config';
import {
  estimatedDailyIdentification,
  takeawayfactorIdentification,
  revenueYHTIdentification,
  explanationYHTIdentification,
} from '../../customize';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';

interface EstimateSalesConclusionProps {
  form: any;
  propertyItem: any;
  required: boolean;
  tiledFormDataRef: any;
  disabled?: boolean;
}
const EstimateSalesConclusion: FC<EstimateSalesConclusionProps> = ({
  propertyItem,
  required,
  form,
  tiledFormDataRef,
  disabled,
}) => {
  const estimateSalesReasonableSelectYHTValue = Form.useWatch(estimateSalesReasonableSelectYHT.keyValue);// 营业额预估合理值的选中值
  const limitYHTCom = tiledFormDataRef?.current?.find((item) => item.controlType === ControlType.ESTIMATE_SALES_TWO_YHT.value);
  const insiderYHTCom = tiledFormDataRef?.current?.find((item) => item.controlType === ControlType.ESTIMATE_SALES_PERSONAL_YHT.value);
  const salesDeliveryCom = tiledFormDataRef?.current?.find((item) => item.controlType === ControlType.ESTIMATE_SALES_DELIVERY_YHT.value); // 外卖预估额定制组件，需要去动态控制其显示数值

  const estimateSalesVal = Form.useWatch(estimatedDailyIdentification, form);
  const estimateSalesUpLimitVal = Form.useWatch(limitYHTCom?.identification || '', form);
  const insiderEstimateSalesVal = Form.useWatch(insiderYHTCom?.identification || '', form);
  const [options, setOptions] = useState<any[]>([]); // 选项值
  const manualSelectChanged: any = useRef(false); // 手动选择过合理值
  const [hasInsiderVal, setHasInsiderVal] = useState<boolean>(false); // 手动输入营业额预估是否有值
  // console.log(`propertyItem`, test, propertyItem);

  // 营业额预估合理值的选中值（当选项、营业额预估合理值选中项改变时）
  const estimatedSalesReasonableValue = useMemo(() => {
    if (!estimateSalesReasonableSelectYHTValue || !options?.length) return 0;
    return options.find((item) => item.value === estimateSalesReasonableSelectYHTValue)?.countValue;
  }, [options, estimateSalesReasonableSelectYHTValue]);

  /*
    如果营业额预估合理值的选中值改变，则同步到几个特殊字段
    -益禾堂预估日商： revenueYHT （数字输入框）
    -外卖系数： takeawayfactor （数字输入框）
    -外卖系数填写说明： explanationYHT （多行文本框）
  */
  useEffect(() => {
    let needCompute = false;
    tiledFormDataRef.current.forEach((item) => {
      if (item.identification === revenueYHTIdentification) {
        // 找到“益禾堂预估日商”字段，并同步值
        form.setFieldValue(item.identification, estimatedSalesReasonableValue);
        setComValue(item, estimatedSalesReasonableValue);
        needCompute = true;
      } else if (item.identification === takeawayfactorIdentification) {
        // 找到“外卖系数”字段，并同步值
        needCompute = methods.dealTakeawayFactor(item, estimateSalesReasonableSelectYHTValue, estimatedSalesReasonableValue);
      } else if (item.identification === explanationYHTIdentification) {
        // 找到“外卖填写说明”字段，系统预估时清空
        methods.dealExplanationYHT(item, estimateSalesReasonableSelectYHTValue);
      }
      /*
        还有其他相关联动处理，放在其他组件中。修改逻辑时考虑是否有影响
        1、“外卖系数”字段的禁用控制，放在NumberInput组件
        2、“外卖系数填写说明”字段的显示隐藏控制，放在TextareaInput组件
      */
    });
    // 对数字输入框赋值后调用计算公式
    needCompute && methods.computationalLogic();
  }, [estimatedSalesReasonableValue]);

  /**
   * @description 营业额预估值（目前共三个字段，其中两个系统预估得出，一个手动输入）变动时相关联动
   *  1、合理值字段的选项变动
   *  2、手动输入营业额有值时，合理值字段自动选中选项“营业额预估(个人）”，并且组件禁用；反之，合理值字段解除禁用
   */
  useEffect(() => {
    const targetOptions: any = [];
    if (isNotEmpty(estimateSalesVal)) {
      // 取label值
      const targetCom = tiledFormDataRef?.current?.find((item) => item.identification === estimatedDailyIdentification);
      targetOptions.push({
        label: `${estimateSalesVal}元（${targetCom?.anotherName || targetCom?.name || '营业额预估值'}）`,
        value: estimatedDailyIdentification,
        detailShowVal: `${estimateSalesVal}元`,
        countValue: estimateSalesVal, // 服务端用
      });
    }
    if (isNotEmpty(estimateSalesUpLimitVal)) {
      const curLabel = methods.getLabel(limitYHTCom);
      targetOptions.push({
        label: `${estimateSalesUpLimitVal}元（${curLabel}）`,
        value: limitYHTCom?.identification,
        detailShowVal: `${estimateSalesUpLimitVal}元`,
        countValue: estimateSalesUpLimitVal, // 服务端用
      });
    }
    const hasInsider = isNotEmpty(insiderEstimateSalesVal); // 是否有手动输入营业额预估
    if (hasInsider) {
      const curLabel = methods.getLabel(insiderYHTCom);
      targetOptions.push({
        label: `${insiderEstimateSalesVal}元（${curLabel}）`,
        value: insiderYHTCom?.identification,
        detailShowVal: `${insiderEstimateSalesVal}元`,
        countValue: insiderEstimateSalesVal, // 服务端用
      });
    }

    let comVal: any = { options: targetOptions };
    if (hasInsider) {
      // 手动输入预估字段有值时，合理值字段自动选中手动预估选项
      comVal = {
        ...comVal,
        [estimateSalesReasonableSelectYHT.keyValue]: insiderYHTCom?.identification || ''
      };
      form.setFieldValue(estimateSalesReasonableSelectYHT.keyValue, insiderYHTCom?.identification || '');
    } else {
      // 无手动营业额
      if (hasInsiderVal) {
        // 手动营业额从有变无，清空合理值选中
        comVal = {
          ...comVal,
          [estimateSalesReasonableSelectYHT.keyValue]: null
        };
        form.setFieldValue(estimateSalesReasonableSelectYHT.keyValue, null);
      }
    }
    setComValue(propertyItem, comVal);
    setOptions(targetOptions);
    // 手动输入预估字段有值时，合理值字段禁用
    setHasInsiderVal(hasInsider);
  }, [estimateSalesVal, estimateSalesUpLimitVal, insiderEstimateSalesVal]);

  const methods = useMethods({
    getLabel: (targetCom: any) => {
      const { anotherName, name } = targetCom || {};
      return anotherName || name;
    },
    handleSelectChange: (val: any, key: string) => {
      manualSelectChanged.current = true;
      setComValue(propertyItem, {
        [key]: val
      });
    },
    /**
     * @description 合理值选中项是否属于系统推算（系统营业额预估1、系统营业额预估2）
     * @param currentVal 合理值的选中项,字符串类型（比如estimatedDailyRevenue）
     * @return true 属于系统推算 false 手动填写值
     */
    isSystemEstimate(currentVal) {
      const identifications: any = [estimatedDailyIdentification];
      if (limitYHTCom?.identification) {
        identifications.push(limitYHTCom.identification);
      }
      return identifications.includes(currentVal);
    },
    /**
     * @description 合理值变动同步影响“外卖系数”字段
     * @param propertyItem 属性项
     * @param esrSelectValue 合理值选中项的标识符，有选中项时有值
     * @param esrValue 合理值选中项的数值
     * @return 是否需要执行计算公式。对“外卖系数”字段值有修改时返回true
     */
    dealTakeawayFactor(propertyItem: any, esrSelectValue: string, esrValue: number) {
      /*
        合理值选中项为系统预估、或系统预估2时，【外卖占比】字段的值=外卖营业额预估的值/合理值，不可修改。
        合理值选中项为人工填入，【外卖占比】需要人工填写不设限制。
      */
      let needCompute = false;
      // 属于系统推算
      if (methods.isSystemEstimate(esrSelectValue)) {
        const deliveryTextvalue = salesDeliveryCom?.textValue; // 外卖营业额预估
        // 找到外卖营业额预估组件且有值、且合理值有值，才进行计算
        if (isNotEmpty(deliveryTextvalue) && esrValue) {
          // 【外卖占比】字段的值=外卖营业额预估的值/合理值，展示为百分比；保留2位
          const calcVal = floorKeep((+deliveryTextvalue * 100 / esrValue), 1, 3, 2);
          form.setFieldValue(propertyItem.identification, calcVal);
          setComValue(propertyItem, calcVal);
          needCompute = true;
        }
      } else if (esrSelectValue) {
        // 选的是其他选项，归属于手动输入，如果是手动选择变动了合理值，则清空外卖占比；回显场景不能清空
        if (manualSelectChanged.current) {
          form.setFieldValue(propertyItem.identification, null);
          setComValue(propertyItem, null);
          needCompute = true;
        }
      }
      return needCompute;
    },
    /**
     * @description 合理值变动同步影响“外卖系数填写说明”字段
     * @param propertyItem 属性项
     * @param esrSelectValue 合理值选中项的标识符，有选中项时有值
     * @return 是否需要执行计算公式。对“外卖系数”字段值有修改时返回true
     */
    dealExplanationYHT(propertyItem: any, esrSelectValue: string) {
      /*
        合理值选中项为系统预估、或系统预估2时，隐藏并清空。隐藏逻辑在其他地方实现
        合理值选中项为人工填入，不处理
      */
      // 属于系统推算
      if (methods.isSystemEstimate(esrSelectValue)) {
        form.setFieldValue(propertyItem.identification, null);
        setComValue(propertyItem, null);
      }
    },
    // 计算公式的逻辑
    computationalLogic () {
      // 查找配置了计算公式的组件进行计算
      const calculateComs = getCalculateComs(tiledFormDataRef.current);
      // 所有的数字输入框组件
      const numInputComs = tiledFormDataRef.current.filter((item: any) => item.controlType === ControlType.INPUT_NUMBER.value);
      // 特殊控件字段：加盟费-益禾堂"franchiseFeeYHT"(下拉框)
      const specialCOms = tiledFormDataRef.current.filter((item: any) => item.identification === 'franchiseFeeYHT');
      // 计算对象：所有的数字输入框组件+加盟费-益禾堂"franchiseFeeYHT"(下拉框)
      const objCalculateComs = [...numInputComs, ...specialCOms];
      calculateHandle(objCalculateComs, calculateComs);
      // 计算的时候只设置了对应的textValue，还需要赋值到Form上
      numInputComs.forEach((item: any) => {
        const { identification, textValue } = item;
        const value = textValue ? JSON.parse(textValue)?.value : ''; // 当前组件的值
        form.setFieldValue(identification, value);
      });
    },
  });

  return (
    <V2FormSelect
      label={estimateSalesReasonableSelectYHT.label}
      name={estimateSalesReasonableSelectYHT.keyValue}
      placeholder='请选择'
      options={options}
      required={required}
      disabled={disabled || hasInsiderVal}
      config={{
        optionLabelProp: 'detailShowVal',
        getPopupContainer: (node) => node.parentNode,
      }}
      onChange={(val) => methods.handleSelectChange(val, estimateSalesReasonableSelectYHT.keyValue)}
    />
  );
};

export default EstimateSalesConclusion;
